import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { api } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogDescription
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../../components/ui/table';
import { 
  Plus, Pencil, Trash2, Loader2, Search, Users, KeyRound, UserPlus, AlertTriangle,
  BarChart3, Trophy, Target, TrendingUp, Gamepad2, Mic, MessageSquare, FileText, 
  Clapperboard, Puzzle, BookOpen, Sparkles, X, GraduationCap, Award, Activity
} from 'lucide-react';
import { toast } from 'sonner';

const StudentsPage = () => {
  const { theme } = useTheme();
  const isDark = theme?.isDark;
  
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterClassId, setFilterClassId] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    class_id: '',
    section_id: '',
    roll_no: ''
  });
  const [bulkFormData, setBulkFormData] = useState({
    class_id: '',
    count: 50,
    default_password: 'student123'
  });
  const [resetFormData, setResetFormData] = useState({
    class_id: '',
    new_password: ''
  });
  const [saving, setSaving] = useState(false);
  const [allSections, setAllSections] = useState([]);
  
  // Student Statistics Dialog State
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentStats, setStudentStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const getClassDisplayName = (cls) => (cls.name || '').replace(/^Class\s+/i, '');

  // Get section display name (strip "Class " prefix and just show class name)
  const getSectionDisplayName = (sec) => {
    const cls = classes.find(c => c.id === sec?.class_id);
    return cls ? (cls.name || '').replace(/^Class\s+/i, '') : (sec?.name || sec?.id || '?');
  };

  const getSectionDisplayNameById = (sectionId) => {
    // Phase 2: No sections table - return class name directly
    const cls = classes.find(c => c.id === sectionId);
    if (!cls) return '-';
    return (cls.name || '').replace(/^Class\s+/i, '');
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [studentsRes, classesRes] = await Promise.all([
        api.get('/users?role=student'),
        api.get('/classes')
      ]);
      setStudents(studentsRes.data);
      setClasses(classesRes.data);
      // Phase 2: No separate sections - classes are combined (1A, 2B, etc.)
      setAllSections([]);
    } catch (err) {
      toast.error('Failed to load data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || (!editingStudent && !formData.password)) {
      toast.error('Please fill all required fields');
      return;
    }

    setSaving(true);
    try {
      if (editingStudent) {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        await api.put(`/users/${editingStudent.id}`, updateData);
        toast.success('Student updated successfully');
      } else {
        await api.post('/users', { ...formData, role: 'student' });
        toast.success('Student created successfully');
      }
      setDialogOpen(false);
      setEditingStudent(null);
      setFormData({ name: '', email: '', password: '', class_id: '', section_id: '', roll_no: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Operation failed');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkCreate = async (e) => {
    e.preventDefault();
    if (!bulkFormData.class_id) {
      toast.error('Please select a class');
      return;
    }
    setSaving(true);
    try {
      const response = await api.post('/students/bulk-create', { 
        class_id: bulkFormData.class_id,
        section_id: bulkFormData.class_id, // use class_id as section_id for compatibility
        count: bulkFormData.count, 
        default_password: bulkFormData.default_password 
      });
      
      // Show detailed feedback
      const { created_count, existing_count, total_count } = response.data;
      if (created_count > 0) {
        toast.success(`Created ${created_count} new students. Total students in class: ${total_count}`);
      } else if (existing_count > 0) {
        toast.info(`Class already has ${existing_count} students. No new students needed.`);
      } else {
        toast.success('Students created successfully');
      }
      
      setBulkDialogOpen(false);
      setBulkFormData({ class_id: '', count: 50, default_password: 'student123' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create students');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPasswords = async (e) => {
    e.preventDefault();
    if (!resetFormData.class_id || !resetFormData.new_password) {
      toast.error('Please select a class and enter new password');
      return;
    }
    const cls = classes.find(c => c.id === resetFormData.class_id);
    if (!window.confirm(`Reset passwords for all students in ${cls?.name || 'this class'}?`)) return;
    setSaving(true);
    try {
      await api.post('/students/reset-passwords', {
        class_id: resetFormData.class_id,
        new_password: resetFormData.new_password
      });
      toast.success('Passwords reset successfully');
      setResetDialogOpen(false);
      setResetFormData({ class_id: '', new_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset passwords');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (student) => {
    setEditingStudent(student);
    setFormData({
      name: student.name,
      email: student.email,
      password: '',
      class_id: student.class_id || '',
      section_id: student.section_id || '',
      roll_no: student.roll_no?.toString() || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (studentId) => {
    if (!window.confirm('Are you sure you want to delete this student?')) return;
    
    try {
      await api.delete(`/users/${studentId}`);
      toast.success('Student deleted successfully');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete student');
    }
  };

  // Handle student click to show statistics
  const handleStudentClick = async (student) => {
    setSelectedStudent(student);
    setStatsDialogOpen(true);
    setLoadingStats(true);
    
    try {
      const response = await api.get(`/students/${student.id}/statistics`);
      setStudentStats(response.data);
    } catch (err) {
      toast.error('Failed to load student statistics');
      setStudentStats(null);
    } finally {
      setLoadingStats(false);
    }
  };

  const getClassName = (classId) => {
    const cls = classes.find(c => c.id === classId);
    return cls?.name || '-';
  };

  const filteredStudents = students.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (s.student_id && s.student_id.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Phase 2: Direct class_id comparison (no sections)
    const matchesClass = filterClassId === 'all' || !filterClassId || s.class_id === filterClassId;
    
    return matchesSearch && matchesClass;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Students</h1>
            <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>Manage student accounts and passwords</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {/* Bulk Create Dialog */}
            <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Bulk Create
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Bulk Create Students</DialogTitle>
                  <DialogDescription>
                    Create multiple students with auto-generated IDs (e.g., 2A01, 2A02, etc.)
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleBulkCreate} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Class *</Label>
                    <Select
                      value={bulkFormData.class_id}
                      onValueChange={(value) => setBulkFormData({ ...bulkFormData, class_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select class (e.g., 2A)" />
                      </SelectTrigger>
                      <SelectContent>
                        {classes.map((cls) => (
                          <SelectItem key={cls.id} value={cls.id}>
                            {cls.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Number of Students (Roll 01 to {bulkFormData.count})</Label>
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={bulkFormData.count}
                      onChange={(e) => setBulkFormData({ ...bulkFormData, count: parseInt(e.target.value) || 50 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Default Password</Label>
                    <Input
                      value={bulkFormData.default_password}
                      onChange={(e) => setBulkFormData({ ...bulkFormData, default_password: e.target.value })}
                      placeholder="Enter default password"
                    />
                    <p className="text-xs text-muted-foreground">
                      Students will login with their ID (e.g., 2A01) and this password
                    </p>
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={saving}>
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Create Students
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            {/* Reset Passwords Dialog */}
            <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className={`${isDark ? 'border-amber-500/30 text-amber-400 hover:bg-amber-500/10' : 'border-amber-200 text-amber-700'}`}>
                  <KeyRound className="h-4 w-4 mr-2" />
                  Reset Passwords
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-amber-500" />
                    Reset Class Passwords
                  </DialogTitle>
                  <DialogDescription>
                    Reset passwords for all students in a class or section
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleResetPasswords} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Class *</Label>
                    <Select
                      value={resetFormData.class_id}
                      onValueChange={(value) => setResetFormData({ ...resetFormData, class_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select class (e.g., 2A)" />
                      </SelectTrigger>
                      <SelectContent>
                        {classes.map((cls) => (
                          <SelectItem key={cls.id} value={cls.id}>
                            {cls.name?.replace(/^Class\s+/i, '') || cls.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>New Password *</Label>
                    <Input
                      type="password"
                      value={resetFormData.new_password}
                      onChange={(e) => setResetFormData({ ...resetFormData, new_password: e.target.value })}
                      placeholder="Enter new password"
                    />
                  </div>
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-amber-50 border border-amber-200'}`}>
                    <p className={`text-sm ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                      ⚠️ This will reset passwords for ALL students in the selected class/section
                    </p>
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={saving} variant="destructive">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Reset Passwords
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>

            {/* Add Single Student Dialog */}
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) {
                setEditingStudent(null);
                setFormData({ name: '', email: '', password: '', class_id: '', section_id: '', roll_no: '' });
              }
            }}>
              <DialogTrigger asChild>
                <Button className={`bg-gradient-to-r ${theme?.colors?.primary || 'from-cyan-500 to-violet-500'} text-white`} data-testid="add-student-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Student
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingStudent ? 'Edit Student' : 'Add New Student'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter student name"
                      data-testid="student-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email / Student ID *</Label>
                    <Input
                      id="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Enter email or student ID (e.g., 2A01)"
                      data-testid="student-email-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Students can login with this email or their student ID
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">
                      Password {editingStudent ? '(leave blank to keep current)' : '*'}
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Enter password"
                      data-testid="student-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Class</Label>
                    <Select
                      value={formData.class_id}
                      onValueChange={(value) => {
                        // Phase 2: class_id is enough, no separate section_id needed
                        setFormData({ ...formData, class_id: value, section_id: value });
                      }}
                    >
                      <SelectTrigger data-testid="student-class-select">
                        <SelectValue placeholder="Select class (e.g., 2A)" />
                      </SelectTrigger>
                      <SelectContent>
                        {classes.map((cls) => (
                          <SelectItem key={cls.id} value={cls.id}>
                            {cls.name?.replace(/^Class\s+/i, '') || cls.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="roll_no">Roll Number *</Label>
                    <Input
                      id="roll_no"
                      type="number"
                      min="1"
                      max="99"
                      value={formData.roll_no}
                      onChange={(e) => setFormData({ ...formData, roll_no: e.target.value })}
                      placeholder="Enter roll number (e.g., 1, 2, 3...)"
                      data-testid="student-roll-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Will be used to generate Student ID (e.g., 1A05 for roll 5 in class 1A)
                    </p>
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={saving} data-testid="save-student-btn">
                      {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      {editingStudent ? 'Update' : 'Create'} Student
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Card className={isDark ? 'premium-card' : ''}>
          <CardHeader className="pb-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by name, email or student ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`pl-9 ${isDark ? 'bg-white/10 border-white/20' : ''}`}
                  data-testid="search-students-input"
                />
              </div>
              <Select
                value={filterClassId}
                onValueChange={(value) => setFilterClassId(value)}
              >
                <SelectTrigger className={`w-[150px] ${isDark ? 'bg-white/10 border-white/20' : ''}`}>
                  <SelectValue placeholder="All classes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Classes</SelectItem>
                  {classes.map((cls) => (
                    <SelectItem key={cls.id} value={cls.id}>
                      {cls.name?.replace(/^Class\s+/i, '') || cls.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-violet-500" />
              </div>
            ) : filteredStudents.length === 0 ? (
              <div className="text-center py-12">
                <Users className={`h-12 w-12 mx-auto mb-4 ${isDark ? 'text-slate-500' : 'text-slate-300'}`} />
                <p className={isDark ? 'text-slate-400' : 'text-slate-500'}>No students found</p>
                <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                  Use "Bulk Create" to add students for a class
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className={isDark ? 'text-slate-300' : ''}>Student ID</TableHead>
                      <TableHead className={isDark ? 'text-slate-300' : ''}>Name</TableHead>
                      <TableHead className={isDark ? 'text-slate-300' : ''}>Class</TableHead>
                      <TableHead className={isDark ? 'text-slate-300' : ''}>Roll No</TableHead>
                      <TableHead className={`text-right ${isDark ? 'text-slate-300' : ''}`}>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredStudents.map((student) => (
                      <TableRow 
                        key={student.id} 
                        className={`${isDark ? 'border-white/10' : ''} cursor-pointer hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors`}
                        onClick={() => handleStudentClick(student)}
                      >
                        <TableCell className={`font-mono font-semibold ${isDark ? 'text-cyan-400' : 'text-indigo-600'}`}>
                          {student.student_id || '-'}
                        </TableCell>
                        <TableCell className={`font-medium ${isDark ? 'text-white' : ''}`}>{student.name}</TableCell>
                        <TableCell className={isDark ? 'text-slate-300' : ''}>
                          {getClassName(student.class_id)}
                        </TableCell>
                        <TableCell className={isDark ? 'text-slate-300' : ''}>
                          {student.roll_no || '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleStudentClick(student)}
                              className={`${isDark ? 'hover:bg-white/10' : ''} text-indigo-500 hover:text-indigo-600`}
                              title="View Statistics"
                            >
                              <BarChart3 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(student)}
                              className={isDark ? 'hover:bg-white/10' : ''}
                              data-testid={`edit-student-${student.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(student.id)}
                              className="text-rose-500 hover:text-rose-600 hover:bg-rose-500/10"
                              data-testid={`delete-student-${student.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Student Statistics Dialog */}
        <Dialog open={statsDialogOpen} onOpenChange={setStatsDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-slate-700 text-white p-0">
            {loadingStats ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-12 w-12 animate-spin text-violet-500" />
              </div>
            ) : studentStats ? (
              <>
                {/* Header */}
                <div className="sticky top-0 z-10 bg-gradient-to-r from-violet-600 via-purple-600 to-pink-600 p-6 rounded-t-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-3 rounded-xl bg-white/20 backdrop-blur">
                        <GraduationCap className="h-8 w-8 text-white" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white">
                          {studentStats.student_info?.name}
                        </h2>
                        <p className="text-white/80">
                          ID: {studentStats.student_info?.student_id} • Class {studentStats.student_info?.class_name}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setStatsDialogOpen(false)}
                      className="text-white/80 hover:text-white hover:bg-white/20"
                    >
                      <X className="h-6 w-6" />
                    </Button>
                  </div>
                </div>

                <div className="p-6 space-y-6">
                  {/* Total Stats Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-2xl p-4 shadow-lg">
                      <Trophy className="h-8 w-8 text-emerald-200 mb-2" />
                      <p className="text-2xl font-bold text-white">{studentStats.totals?.total_score}</p>
                      <p className="text-sm text-white/80">Total Score</p>
                    </div>
                    <div className="bg-gradient-to-br from-blue-500 to-blue-700 rounded-2xl p-4 shadow-lg">
                      <Target className="h-8 w-8 text-blue-200 mb-2" />
                      <p className="text-2xl font-bold text-white">{studentStats.totals?.total_attempts}</p>
                      <p className="text-sm text-white/80">Total Attempts</p>
                    </div>
                    <div className="bg-gradient-to-br from-violet-500 to-violet-700 rounded-2xl p-4 shadow-lg">
                      <Award className="h-8 w-8 text-violet-200 mb-2" />
                      <p className="text-2xl font-bold text-white">{studentStats.totals?.module_score}</p>
                      <p className="text-sm text-white/80">Module Score</p>
                    </div>
                    <div className="bg-gradient-to-br from-pink-500 to-pink-700 rounded-2xl p-4 shadow-lg">
                      <Gamepad2 className="h-8 w-8 text-pink-200 mb-2" />
                      <p className="text-2xl font-bold text-white">{studentStats.totals?.fun_learning_score}</p>
                      <p className="text-sm text-white/80">Fun Learning</p>
                    </div>
                  </div>

                  {/* Test Modules Performance */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-cyan-400" />
                      Test Modules Performance
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* TTS Module */}
                      <div className="bg-gradient-to-br from-cyan-900/50 to-cyan-950/50 rounded-xl p-4 border border-cyan-500/20">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-2 rounded-lg bg-cyan-500">
                            <Mic className="h-5 w-5 text-white" />
                          </div>
                          <h4 className="font-semibold text-white">Text to Speech</h4>
                        </div>
                        <div className="grid grid-cols-4 gap-2 text-center">
                          <div className="bg-white/5 rounded-lg p-2">
                            <p className="text-lg font-bold text-cyan-400">{studentStats.module_stats?.tts?.attempts || 0}</p>
                            <p className="text-xs text-slate-400">Attempts</p>
                          </div>
                          <div className="bg-white/5 rounded-lg p-2">
                            <p className="text-lg font-bold text-emerald-400">{studentStats.module_stats?.tts?.avg_score || 0}%</p>
                            <p className="text-xs text-slate-400">Average</p>
                          </div>
                          <div className="bg-white/5 rounded-lg p-2">
                            <p className="text-lg font-bold text-amber-400">{studentStats.module_stats?.tts?.best_score || 0}%</p>
                            <p className="text-xs text-slate-400">Best</p>
                          </div>
                          <div className="bg-white/5 rounded-lg p-2">
                            <p className="text-lg font-bold text-violet-400">{studentStats.module_stats?.tts?.total_score || 0}</p>
                            <p className="text-xs text-slate-400">Total</p>
                          </div>
                        </div>
                      </div>

                      {/* STT Module */}
                      <div className="bg-gradient-to-br from-emerald-900/50 to-emerald-950/50 rounded-xl p-4 border border-emerald-500/20">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-2 rounded-lg bg-emerald-500">
                            <MessageSquare className="h-5 w-5 text-white" />
                          </div>
                          <h4 className="font-semibold text-white">Speech to Text</h4>
                        </div>
                        <div className="grid grid-cols-4 gap-2 text-center">
                          <div className="bg-white/5 rounded-lg p-2">
                            <p className="text-lg font-bold text-cyan-400">{studentStats.module_stats?.stt?.attempts || 0}</p>
                            <p className="text-xs text-slate-400">Attempts</p>
                          </div>
                          <div className="bg-white/5 rounded-lg p-2">
                            <p className="text-lg font-bold text-emerald-400">{studentStats.module_stats?.stt?.avg_score || 0}%</p>
                            <p className="text-xs text-slate-400">Average</p>
                          </div>
                          <div className="bg-white/5 rounded-lg p-2">
                            <p className="text-lg font-bold text-amber-400">{studentStats.module_stats?.stt?.best_score || 0}%</p>
                            <p className="text-xs text-slate-400">Best</p>
                          </div>
                          <div className="bg-white/5 rounded-lg p-2">
                            <p className="text-lg font-bold text-violet-400">{studentStats.module_stats?.stt?.total_score || 0}</p>
                            <p className="text-xs text-slate-400">Total</p>
                          </div>
                        </div>
                      </div>

                      {/* MCQ Module */}
                      <div className="bg-gradient-to-br from-violet-900/50 to-violet-950/50 rounded-xl p-4 border border-violet-500/20">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-2 rounded-lg bg-violet-500">
                            <FileText className="h-5 w-5 text-white" />
                          </div>
                          <h4 className="font-semibold text-white">MCQ Quiz</h4>
                        </div>
                        <div className="grid grid-cols-4 gap-2 text-center">
                          <div className="bg-white/5 rounded-lg p-2">
                            <p className="text-lg font-bold text-cyan-400">{studentStats.module_stats?.mcq?.attempts || 0}</p>
                            <p className="text-xs text-slate-400">Attempts</p>
                          </div>
                          <div className="bg-white/5 rounded-lg p-2">
                            <p className="text-lg font-bold text-emerald-400">{studentStats.module_stats?.mcq?.avg_score || 0}%</p>
                            <p className="text-xs text-slate-400">Average</p>
                          </div>
                          <div className="bg-white/5 rounded-lg p-2">
                            <p className="text-lg font-bold text-amber-400">{studentStats.module_stats?.mcq?.best_score || 0}%</p>
                            <p className="text-xs text-slate-400">Best</p>
                          </div>
                          <div className="bg-white/5 rounded-lg p-2">
                            <p className="text-lg font-bold text-violet-400">{studentStats.module_stats?.mcq?.total_score || 0}</p>
                            <p className="text-xs text-slate-400">Total</p>
                          </div>
                        </div>
                      </div>

                      {/* VMCQ Module */}
                      <div className="bg-gradient-to-br from-rose-900/50 to-rose-950/50 rounded-xl p-4 border border-rose-500/20">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="p-2 rounded-lg bg-rose-500">
                            <Clapperboard className="h-5 w-5 text-white" />
                          </div>
                          <h4 className="font-semibold text-white">Cinematic MCQ</h4>
                        </div>
                        <div className="grid grid-cols-4 gap-2 text-center">
                          <div className="bg-white/5 rounded-lg p-2">
                            <p className="text-lg font-bold text-cyan-400">{studentStats.module_stats?.vmcq?.attempts || 0}</p>
                            <p className="text-xs text-slate-400">Attempts</p>
                          </div>
                          <div className="bg-white/5 rounded-lg p-2">
                            <p className="text-lg font-bold text-emerald-400">{studentStats.module_stats?.vmcq?.avg_score || 0}%</p>
                            <p className="text-xs text-slate-400">Average</p>
                          </div>
                          <div className="bg-white/5 rounded-lg p-2">
                            <p className="text-lg font-bold text-amber-400">{studentStats.module_stats?.vmcq?.best_score || 0}%</p>
                            <p className="text-xs text-slate-400">Best</p>
                          </div>
                          <div className="bg-white/5 rounded-lg p-2">
                            <p className="text-lg font-bold text-violet-400">{studentStats.module_stats?.vmcq?.total_score || 0}</p>
                            <p className="text-xs text-slate-400">Total</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Fun Learning Stats */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <Gamepad2 className="h-5 w-5 text-pink-400" />
                      Fun Learning Games
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-xl p-4 border border-white/5">
                        <div className="flex items-center gap-2 mb-3">
                          <Puzzle className="h-5 w-5 text-cyan-400" />
                          <span className="text-sm font-medium text-white">Picture Match</span>
                        </div>
                        <p className="text-2xl font-bold text-cyan-400">{studentStats.fun_learning_stats?.picture_match?.score || 0}</p>
                        <p className="text-xs text-slate-400">{studentStats.fun_learning_stats?.picture_match?.plays || 0} plays</p>
                      </div>
                      <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-xl p-4 border border-white/5">
                        <div className="flex items-center gap-2 mb-3">
                          <BookOpen className="h-5 w-5 text-violet-400" />
                          <span className="text-sm font-medium text-white">Context Word</span>
                        </div>
                        <p className="text-2xl font-bold text-violet-400">{studentStats.fun_learning_stats?.context_word?.score || 0}</p>
                        <p className="text-xs text-slate-400">{studentStats.fun_learning_stats?.context_word?.plays || 0} plays</p>
                      </div>
                      <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-xl p-4 border border-white/5">
                        <div className="flex items-center gap-2 mb-3">
                          <Sparkles className="h-5 w-5 text-amber-400" />
                          <span className="text-sm font-medium text-white">Spelling Bee</span>
                        </div>
                        <p className="text-2xl font-bold text-amber-400">{studentStats.fun_learning_stats?.spelling_bee?.score || 0}</p>
                        <p className="text-xs text-slate-400">{studentStats.fun_learning_stats?.spelling_bee?.plays || 0} plays</p>
                      </div>
                      <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-xl p-4 border border-white/5">
                        <div className="flex items-center gap-2 mb-3">
                          <FileText className="h-5 w-5 text-emerald-400" />
                          <span className="text-sm font-medium text-white">Sentence Builder</span>
                        </div>
                        <p className="text-2xl font-bold text-emerald-400">{studentStats.fun_learning_stats?.sentence_builder?.score || 0}</p>
                        <p className="text-xs text-slate-400">{studentStats.fun_learning_stats?.sentence_builder?.plays || 0} plays</p>
                      </div>
                    </div>
                  </div>

                  {/* Recent Activity */}
                  {studentStats.recent_activity && studentStats.recent_activity.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <Activity className="h-5 w-5 text-emerald-400" />
                        Recent Activity
                      </h3>
                      <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl overflow-hidden border border-white/10">
                        <div className="divide-y divide-white/5">
                          {studentStats.recent_activity.map((activity, index) => (
                            <div key={index} className="flex items-center justify-between p-3 hover:bg-white/5">
                              <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg ${
                                  activity.module_type === 'tts' ? 'bg-cyan-500/20' :
                                  activity.module_type === 'stt' ? 'bg-emerald-500/20' :
                                  activity.module_type === 'mcq' ? 'bg-violet-500/20' :
                                  'bg-rose-500/20'
                                }`}>
                                  {activity.module_type === 'tts' && <Mic className="h-4 w-4 text-cyan-400" />}
                                  {activity.module_type === 'stt' && <MessageSquare className="h-4 w-4 text-emerald-400" />}
                                  {activity.module_type === 'mcq' && <FileText className="h-4 w-4 text-violet-400" />}
                                  {activity.module_type === 'vmcq' && <Clapperboard className="h-4 w-4 text-rose-400" />}
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-white uppercase">{activity.module_type}</p>
                                  <p className="text-xs text-slate-400">
                                    {activity.created_at ? new Date(activity.created_at).toLocaleDateString() : '-'}
                                  </p>
                                </div>
                              </div>
                              <p className="text-lg font-bold text-emerald-400">{activity.score}%</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="py-20 text-center text-slate-400">
                <p>Failed to load statistics</p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default StudentsPage;
